import { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { projects as mockProjects } from '../mock';
import { MapPin, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [projects, setProjects] = useState(mockProjects);

  useEffect(() => {
    const loadProjects = async () => {
      const data = await api.getProjects(mockProjects);
      if (data.length > 0) setProjects(data);
    };
    loadProjects();
  }, []);

  const categories = [
    { value: 'all', label: 'Todos os Condomínios' },
    { value: 'Reforma de Fachada', label: 'Reforma de Fachada' },
    { value: 'Manutenção Predial', label: 'Manutenção Predial' },
    { value: 'Impermeabilizações', label: 'Impermeabilizações' },
    { value: 'Hidráulica', label: 'Hidráulica' },
    { value: 'Elétrica', label: 'Elétrica' },
    { value: 'Pintura e Alvenaria', label: 'Pintura e Alvenaria' },
    { value: 'Manutenção de Telhados', label: 'Manutenção de Telhados' }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#15448A] via-[#1e5aa8] to-[#0d2f5e] text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-[#F47A20]/10 text-[#F47A20] hover:bg-[#F47A20]/20">
              Nosso Portfólio
            </Badge>
            <h1 className="text-5xl font-bold">
              Condomínios Atendidos pela LUVCORP
            </h1>
            <p className="text-xl text-blue-100">
              Conheça os condomínios que confiam na LUVCORP no Rio de Janeiro. Cada projeto representa nosso compromisso com qualidade, segurança e satisfação do cliente.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Filter Tabs */}
          <div className="mb-12">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
              <TabsList className="w-full justify-start flex-wrap h-auto gap-2 bg-gray-100 p-2">
                {categories.map((category) => (
                  <TabsTrigger
                    key={category.value}
                    value={category.value}
                    className="data-[state=active]:bg-[#15448A] data-[state=active]:text-white"
                  >
                    {category.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Projects Count */}
          <div className="mb-8">
            <p className="text-gray-600">
              Exibindo <span className="font-semibold text-gray-900">{filteredProjects.length}</span> condomínio(s)
            </p>
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#15448A]/30 bg-white">
                <CardContent className="p-8 space-y-4">
                  <div className="flex items-center justify-center">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white font-bold text-2xl group-hover:scale-110 transition-transform">
                      <Building2 size={28} />
                    </div>
                  </div>
                  
                  <div className="space-y-3 text-center">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#15448A] transition-colors">
                      {project.title}
                    </h3>
                    
                    <div className="flex items-start justify-center space-x-2 text-gray-600">
                      <MapPin size={18} className="text-[#F47A20] mt-0.5 flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{project.location}</span>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-200 flex justify-center">
                      <Badge variant="outline" className="border-[#15448A]/30 text-[#15448A]">
                        {project.category}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <svg className="w-24 h-24 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum condomínio encontrado</h3>
              <p className="text-gray-600">Tente selecionar outra categoria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-gradient-to-br from-[#15448A] to-[#0d2f5e] text-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              <span className="text-[#F47A20]">Números</span> que Falam por Si
            </h2>
            <p className="text-blue-100">Resultados concretos de anos de dedicação e excelência.</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">45+</div>
              <div className="text-blue-100 text-lg">Condomínios Atendidos</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">5+</div>
              <div className="text-blue-100 text-lg">Anos de Experiência</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">95%</div>
              <div className="text-blue-100 text-lg">Satisfação dos Clientes</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">0</div>
              <div className="text-blue-100 text-lg">Acidentes de Trabalho</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">
              Seu Condomínio Pode Ser o Próximo
            </h2>
            <p className="text-xl text-gray-600">
              Entre em contato e faça parte de nosso portfólio de sucesso.
            </p>
            <Link to="/contato">
              <Button size="lg" className="bg-[#F47A20] hover:bg-[#e66a10] text-white shadow-lg text-lg px-10 py-7">
                Solicitar Orçamento
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;

